import React, { useState, useEffect } from 'react';
import Button from '../common/Button';
import { useToast } from '../common/Toast';
import { Calendar, CheckCircle, Layers } from 'lucide-react';
import api from '../../services/api';
import { format } from 'date-fns';
import CalendrierInterventions from './CalendrierInterventions';

const InterventionForm = ({ intervention, onSuccess, onCancel, preSelectedDate }) => {
  const [formData, setFormData] = useState({
    date: preSelectedDate ? format(preSelectedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
    heureDebut: '09:00',
    heureFin: '11:00',
    beneficiaireId: '',
    auxiliaireId: '',
    type: '',
    statut: 'planifiee'
  });
  const [auxiliaires, setAuxiliaires] = useState([]);
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCalendrier, setShowCalendrier] = useState(false);
  const [multipleMode, setMultipleMode] = useState(false); // NOUVEAU
  const [selectedCreneaux, setSelectedCreneaux] = useState([]); // NOUVEAU
  const { showToast } = useToast();

  const typesIntervention = [
    "Aide à la toilette",
    "Préparation repas",
    "Aide au repas",
    "Courses",
    "Accompagnement médical",
    "Entretien logement",
    "Lever et petit-déjeuner",
    "Dîner et coucher",
    "Aide à la mobilité",
    "Activités / Promenade",
    "Aide à la toilette + repas",
    "Courses + préparation repas"
  ];

  useEffect(() => {
    loadData();
    if (intervention) {
      setFormData({
        date: format(new Date(intervention.date), 'yyyy-MM-dd'),
        heureDebut: intervention.heureDebut,
        heureFin: intervention.heureFin,
        beneficiaireId: intervention.beneficiaireId._id || intervention.beneficiaireId,
        auxiliaireId: intervention.auxiliaireId._id || intervention.auxiliaireId,
        type: intervention.type,
        statut: intervention.statut
      });
      setMultipleMode(false); // Pas de mode multiple en édition
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervention]);

  const loadData = async () => {
    try {
      const [auxRes, benRes] = await Promise.all([
        api.get('/users/auxiliaires'),
        api.get('/beneficiaires')
      ]);
      setAuxiliaires(auxRes.data);
      setBeneficiaires(benRes.data);
    } catch (error) {
      showToast('Erreur chargement données', 'error');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleBeneficiaireChange = (e) => {
    setFormData({
      ...formData,
      beneficiaireId: e.target.value
    });
    
    if (e.target.value) {
      setShowCalendrier(true);
    }
  };

  // NOUVEAU : Toggle mode multiple
  const handleToggleMultipleMode = () => {
    setMultipleMode(!multipleMode);
    setSelectedCreneaux([]);
    if (!multipleMode) {
      showToast('Mode sélection multiple activé', 'info');
    }
  };

  // NOUVEAU : Sélection simple (ancien comportement)
  const handleSelectCreneau = (creneau) => {
    if (!multipleMode) {
      setFormData({
        ...formData,
        date: creneau.date,
        heureDebut: creneau.heureDebut,
        heureFin: creneau.heureFin
      });
      showToast('Créneau sélectionné !', 'success');
    }
  };

  // NOUVEAU : Sélection multiple
  const handleSelectMultiple = (creneaux) => {
    setSelectedCreneaux(creneaux);
  };

  // NOUVEAU : Soumission multiple
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (multipleMode && selectedCreneaux.length === 0) {
      showToast('Sélectionnez au moins un créneau', 'error');
      return;
    }

    if (!multipleMode && formData.heureDebut >= formData.heureFin) {
      showToast('L\'heure de fin doit être après l\'heure de début', 'error');
      return;
    }

    setLoading(true);

    try {
      if (multipleMode) {
        // Créer plusieurs interventions
        const interventionsData = selectedCreneaux.map(creneau => ({
          date: creneau.date,
          heureDebut: creneau.heureDebut,
          heureFin: creneau.heureFin,
          beneficiaireId: formData.beneficiaireId,
          auxiliaireId: formData.auxiliaireId,
          type: formData.type,
          statut: 'planifiee'
        }));

        await api.post('/interventions/bulk', { interventions: interventionsData });
        showToast(`${interventionsData.length} intervention${interventionsData.length > 1 ? 's' : ''} créée${interventionsData.length > 1 ? 's' : ''} avec succès`, 'success');
      } else {
        // Créer ou modifier une seule intervention
        if (intervention) {
          await api.put(`/interventions/${intervention._id}`, formData);
          showToast('Intervention modifiée avec succès', 'success');
        } else {
          await api.post('/interventions', formData);
          showToast('Intervention créée avec succès', 'success');
        }
      }
      onSuccess();
    } catch (error) {
      showToast(error.response?.data?.message || 'Erreur lors de l\'enregistrement', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Sélection bénéficiaire */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Bénéficiaire *
        </label>
        <select
          name="beneficiaireId"
          required
          value={formData.beneficiaireId}
          onChange={handleBeneficiaireChange}
          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium"
        >
          <option value="">Sélectionner un bénéficiaire...</option>
          {beneficiaires.map(benef => (
            <option key={benef._id} value={benef._id}>
              {benef.prenom} {benef.nom} - {benef.adresse.rue}
            </option>
          ))}
        </select>
      </div>

      {/* NOUVEAU : Toggle mode multiple */}
      {!intervention && showCalendrier && formData.beneficiaireId && (
        <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers size={20} className="text-purple-600" />
              <div>
                <div className="text-sm font-bold text-gray-800">Sélection multiple</div>
                <div className="text-xs text-gray-600">Créer plusieurs interventions en une fois</div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleToggleMultipleMode}
              className={`px-4 py-2 rounded-lg font-bold text-sm transition-all active:scale-95 ${
                multipleMode
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-white border-2 border-purple-300 text-purple-600'
              }`}
            >
              {multipleMode ? 'Activé' : 'Désactivé'}
            </button>
          </div>
        </div>
      )}

      {/* CALENDRIER DES INTERVENTIONS */}
      {showCalendrier && formData.beneficiaireId && (
        <div className="border-2 border-blue-200 rounded-xl p-4 bg-blue-50">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={20} className="text-blue-600" />
            <h3 className="font-bold text-gray-800">
              {multipleMode ? 'Sélectionnez plusieurs créneaux' : 'Interventions et créneaux disponibles'}
            </h3>
          </div>
          <CalendrierInterventions
            beneficiaireId={formData.beneficiaireId}
            dateSelectionnee={formData.date}
            onSelectCreneau={handleSelectCreneau}
            multipleSelection={multipleMode}
            selectedCreneaux={selectedCreneaux}
            onSelectMultiple={handleSelectMultiple}
          />
        </div>
      )}

      {/* Affichage créneaux sélectionnés en mode multiple */}
      {multipleMode && selectedCreneaux.length > 0 && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-300 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle size={20} className="text-purple-600" />
            <div className="font-bold text-gray-800">
              {selectedCreneaux.length} créneau{selectedCreneaux.length > 1 ? 'x' : ''} sélectionné{selectedCreneaux.length > 1 ? 's' : ''}
            </div>
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {selectedCreneaux.map((creneau, idx) => (
              <div key={idx} className="bg-white border border-purple-300 rounded-lg p-2 flex items-center gap-2">
                <CheckCircle size={16} className="text-purple-600 flex-shrink-0" />
                <div className="text-sm text-gray-700 flex-1">
                  <span className="font-bold">
                    {new Date(creneau.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </span>
                  {' : '}
                  {creneau.heureDebut} - {creneau.heureFin}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Date et heures - Mode simple uniquement */}
      {!multipleMode && formData.date && formData.heureDebut && (
        <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4">
          <div className="text-sm font-bold text-green-800 mb-2">
            ✅ Créneau sélectionné
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                type="date"
                name="date"
                required
                value={formData.date}
                onChange={handleChange}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-medium text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Heure début
              </label>
              <input
                type="time"
                name="heureDebut"
                required
                value={formData.heureDebut}
                onChange={handleChange}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-medium text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Heure fin
              </label>
              <input
                type="time"
                name="heureFin"
                required
                value={formData.heureFin}
                onChange={handleChange}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-medium text-sm"
              />
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type d'intervention *
          </label>
          <select
            name="type"
            required
            value={formData.type}
            onChange={handleChange}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium"
          >
            <option value="">Sélectionner...</option>
            {typesIntervention.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Auxiliaire de vie *
          </label>
          <select
            name="auxiliaireId"
            required
            value={formData.auxiliaireId}
            onChange={handleChange}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium"
          >
            <option value="">Sélectionner une auxiliaire...</option>
            {auxiliaires.map(aux => (
              <option key={aux._id} value={aux._id}>
                {aux.prenom} {aux.nom}
              </option>
            ))}
          </select>
        </div>
      </div>

      {intervention && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Statut
          </label>
          <select
            name="statut"
            value={formData.statut}
            onChange={handleChange}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium"
          >
            <option value="planifiee">Planifiée</option>
            <option value="arrivee">En cours</option>
            <option value="effectuee">Effectuée</option>
            <option value="annulee">Annulée</option>
          </select>
        </div>
      )}

      <div className="flex gap-3 pt-4 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          fullWidth
        >
          Annuler
        </Button>
        <Button
          type="submit"
          disabled={loading || (multipleMode && selectedCreneaux.length === 0)}
          fullWidth
        >
          {loading ? 'Enregistrement...' : (
            multipleMode 
              ? `Créer ${selectedCreneaux.length} intervention${selectedCreneaux.length > 1 ? 's' : ''}`
              : (intervention ? 'Modifier' : 'Créer')
          )}
        </Button>
      </div>
    </form>
  );
};

export default InterventionForm;
